import { ACTION_MODE, BOOKING_STATUS, DEFAULT_SORT_FIELD, FIRST_PAGE, ID_FIELD, PER_PAGE, SORT_ORDER } from 'app/constants';
import { bookingService, commonService } from 'app/services';
import { onlyDateTemplate, useNoTemplate } from 'app/views/common/common-template';
import { HttpStatusCode } from 'axios';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Paginator } from 'primereact/paginator';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
// import { useSelector } from 'react-redux';
import AddEditBooking from './add-edit';
export default function BookingManagement() {
    const { t } = useTranslation();
    const addEditBooking = useRef();
    const inputSearch = useRef();
    const [first, setPaginator] = useState(FIRST_PAGE);
    const [searchFieldTyping, setSearchFieldTyping] = useState('');
    const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
    const [sortOrder, setSortOrder] = useState(SORT_ORDER.DESC);
    const [total, setTotalRecords] = useState(0);
    const [data, setData] = useState([]);
    const [rows] = useState(PER_PAGE);
    const noTemplate = useNoTemplate();
    //#region tooltip
    const tooltip = useRef();
    const [tooltipEnter, tooltipLeave] = useMemo(() => [(e) => tooltip.current.show(e), (e) => tooltip.current.hide(e)], []);
    //#end region
    /**
     * fetch data from api
     * @param {Object} input
     * @returns
     */
    const fetchData = async (input) => {
        commonService.preloader.show();
        const payload = {
            searchField: searchFieldTyping.trim(),
            sortField,
            sortOrder,
            first,
            rows,
            ...input
        };
        const data = await bookingService.search(payload);
        setData(data.data);
        setTotalRecords(data.total);
        commonService.preloader.hide();
        return true;
    };
    /**
     * re-load table and execute function to search data
     * @function fetchData Handle and display data on table
     */
    const onSearch = (e) => {
        e.preventDefault();
        setPaginator(FIRST_PAGE);
        fetchData({ first: FIRST_PAGE });
    };
    /**
     * re-load table and execute function to paginator data
     * @function fetchData Handle and display data on table
     */
    const onPage = (e) => {
        setPaginator(e.first);
        fetchData({ first: e.first });
    };
    /**
     * Handle sort option
     * @function fetchData Handle and Display data on table
     */
    const onSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
        fetchData({ sortField: e.sortField, sortOrder: e.sortOrder });
    };
    /**
     * Edit user
     * @param {Object} rowData contains rows data
     */
    const onEdit = async (rowData) => {
        const detail = await bookingService.detail({ ID: rowData.ID });
        addEditBooking.current.show({ mode: ACTION_MODE.EDIT, ...detail.data.payload });
    };
    /**
     * Handle Delete Action
     * @param {Number} ID ID action
     */
    const remove = async (ID) => {
        const result = await commonService.modal.showConfirm({
            header: t('LABEL_DELETE_ITEM'),
            message: t('M016'),
            img: 'delete-message',
            customClass: 'warning-confirm-delete px-0'
        });
        if (result) {
            commonService.preloader.show();
            const res = await bookingService.deleteBooking({ ID });
            if (res.status === HttpStatusCode.Ok) {
                commonService.toast.show({
                    severity: 'success',
                    summary: t('INFO_MESSAGE'),
                    detail: t('M018')
                });
                fetchData({ first: FIRST_PAGE });
                setPaginator(FIRST_PAGE);
                commonService.preloader.hide();
                return true;
            }
            commonService.preloader.hide();
            return commonService.toast.show({
                severity: 'error',
                summary: t('ERROR_MESSAGE'),
                detail: t('M017')
            });
        }
    };
    /**
     * Handle Confirm booking
     * @param {Number} ID ID action
     */
    const onConfirm = async (ID) => {
        commonService.preloader.show();
        const res = await bookingService.confirm({ ID, Status: BOOKING_STATUS.CONFIRM });
        if (res.status === HttpStatusCode.Ok) {
            commonService.toast.show({
                severity: 'success',
                summary: t('INFO_MESSAGE'),
                detail: t('M011')
            });
            fetchData({ first: FIRST_PAGE });
            setPaginator(FIRST_PAGE);
            commonService.preloader.hide();
            return true;
        }
        commonService.preloader.hide();
        return commonService.toast.show({
            severity: 'error',
            summary: t('ERROR_MESSAGE'),
            detail: t('M015')
        });
    };
    /**
     * Handle Reject booking
     * @param {Number} ID ID action
     */
    const onReject = async (ID) => {
        commonService.preloader.show();
        const res = await bookingService.confirm({ ID, Status: BOOKING_STATUS.CANCEL });
        if (res.status === HttpStatusCode.Ok) {
            commonService.toast.show({
                severity: 'success',
                summary: t('INFO_MESSAGE'),
                detail: t('M014')
            });
            fetchData({ first: FIRST_PAGE });
            setPaginator(FIRST_PAGE);
            commonService.preloader.hide();
            return true;
        }
        commonService.preloader.hide();
        return commonService.toast.show({
            severity: 'error',
            summary: t('ERROR_MESSAGE'),
            detail: t('M019')
        });
    };
    /**
     * view detail user
     * @param {Object} rowData contains rows data
     */
    const onDetail = async (rowData) => {
        const detail = await bookingService.detail({ ID: rowData.ID });
        addEditBooking.current.show({ mode: ACTION_MODE.DETAIL, ...detail.data.payload });
    };
    /**
     * Action template
     * @param {Object} rowData contains rows data
     */
    const actionTemplate = (rowData) => {
        return (
            <div className='d-flex justify-content-center'>
                <Button
                    data-pr-tooltip={t('BTN_UPDATE')}
                    onMouseEnter={tooltipEnter}
                    onMouseLeave={tooltipLeave}
                    variant='default'
                    className='btn-action-template'
                    onClick={() => onEdit(rowData)}>
                    <span className='icon edit fs-md' />
                </Button>
                <Button
                    data-pr-tooltip={t('LABEL_DETAIL')}
                    onMouseEnter={tooltipEnter}
                    onMouseLeave={tooltipLeave}
                    variant='default'
                    className='btn-action-template'
                    onClick={() => onDetail(rowData)}>
                    <span className='icon detail fs-md' />
                </Button>
                <Button
                    data-pr-tooltip={t('LABEL_TOOLTIP_ICON_REMOVE')}
                    onMouseEnter={tooltipEnter}
                    onMouseLeave={tooltipLeave}
                    variant='default'
                    className='btn-action-template'
                    onClick={() => {
                        remove(rowData.ID);
                    }}>
                    <span className='icon delete fs-md' />
                </Button>
                {rowData.Status === BOOKING_STATUS.WAITING && (
                    <div>
                        <Button
                            data-pr-tooltip={t('LABEL_CONFIRM')}
                            onMouseEnter={tooltipEnter}
                            onMouseLeave={tooltipLeave}
                            variant='default'
                            className='btn-action-template'
                            onClick={() => {
                                onConfirm(rowData.ID);
                            }}>
                            <span className='icon accepted fs-md' />
                        </Button>
                        <Button
                            data-pr-tooltip={t('LABEL_CANCEL')}
                            onMouseEnter={tooltipEnter}
                            onMouseLeave={tooltipLeave}
                            variant='default'
                            className='btn-action-template'
                            onClick={() => {
                                onReject(rowData.ID);
                            }}>
                            <span className='icon rejected fs-md' />
                        </Button>
                    </div>
                )}
            </div>
        );
    };
    // /**
    //  * Add User
    //  */
    // const onAddBooking = () => {
    //     addEditBooking.current.show({ mode: ACTION_MODE.ADD });
    // };
    /**
     * fetch data when change info user
     * @param {Boolean} flag
     */
    const onChangeBooking = (flag) => {
        if (flag) {
            setPaginator(FIRST_PAGE);
            setSortField(DEFAULT_SORT_FIELD);
            setSortOrder(SORT_ORDER.DESC);
            fetchData({ sortField: DEFAULT_SORT_FIELD, sortOrder: SORT_ORDER.DESC, first: FIRST_PAGE });
        }
    };
    const statusTemplate = (rowData) => {
        return (
            <div>
                {rowData.Status === BOOKING_STATUS.CANCEL
                    ? t('LABEL_CANCEL')
                    : rowData.Status === BOOKING_STATUS.CONFIRM
                      ? t('LABEL_CONFIRM')
                      : t('LABEL_WAITING')}
            </div>
        );
    };
    const adultTemplate = (rowData) => {
        return <div>{`${rowData.Adult} Adults - ${rowData.Child} Children`}</div>;
    };
    /**
     * Fetch first render data
     */
    useEffect(() => {
        commonService.preloader.show();
        fetchData();
        inputSearch.current.focus();
    }, []);
    return (
        <React.Fragment>
            <div
                className={
                    total <= rows
                        ? 'page-table d-flex flex-column fill-height table-common px-3'
                        : 'page-table d-flex flex-column fill-height table-pagination px-3'
                }>
                <div className='d-flex flex-column fill-height px-0 pt-2'>
                    <div className='h-100 d-flex flex-column rounded'>
                        <div className='col-12 row mx-0 py-2'>
                            <div className='w-55 py-2 row mx-0 px-0'>
                                <h1 className='my-0 fs-xxl fw-bold text-primary w-100 px-0 align-items-center d-flex'>
                                    {t('LABEL_BOOKING')}
                                </h1>
                            </div>
                            <div className='w-25 px-0 h-40-px'>
                                <form onSubmit={onSearch} className='position-relative width-input' autoComplete='off'>
                                    <InputText
                                        placeholder={t('PLACEHOLDER_ENTER_KEY_SEARCH')}
                                        className='ps-3 w-100'
                                        value={searchFieldTyping}
                                        ref={inputSearch}
                                        onChange={(e) => setSearchFieldTyping(e.target.value)}
                                    />
                                    <Button variant='custom-inline' type='submit'>
                                        <span className='icon search fs-md icon-search'></span>
                                    </Button>
                                </form>
                            </div>
                            {/* <div className='w-20 px-0 d-flex justify-content-end'>
                                <Button className='btn-action-sm px-4' variant='default' onClick={onAddBooking}>
                                    {t('BTN_ADD_BOOKING')}
                                </Button>
                            </div> */}
                        </div>
                        <DataTable
                            value={data}
                            rows={rows}
                            first={first}
                            dataKey={ID_FIELD}
                            lazy
                            scrollable
                            totalRecords={total}
                            sortOrder={sortOrder}
                            sortField={sortField}
                            onSort={onSort}
                            onPage={onPage}
                            scrollHeight='flex'
                            className='fs-md mh-0 pt-2 fs-lg overflow-hidden table-container p-table-common'
                            emptyMessage={t('M008')}
                            tableClassName='table-striped table-hover'>
                            <Column
                                headerClassName='px-3'
                                header={t('TABLE_LABEL_NO')}
                                body={noTemplate}
                                className='fs-md px-3'
                                style={{ width: '5%' }}
                            />
                            <Column
                                field='Name'
                                headerClassName='px-3'
                                header={t('TABLE_LABEL_NAME')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '12.5%' }}
                            />
                            <Column
                                field='Email'
                                headerClassName='px-3'
                                header={t('TABLE_LABEL_EMAIL')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '12.5%' }}
                            />
                            <Column
                                field='Phone'
                                headerClassName='px-3'
                                header={t('TABLE_LABEL_PHONE_NUMBER')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '11.5%' }}
                            />
                            <Column
                                field='Adult'
                                body={adultTemplate}
                                headerClassName='px-3'
                                header={t('TABLE_LABEL_ADULT')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '10%' }}
                            />
                            <Column
                                field='Arrival'
                                headerClassName='px-3'
                                body={onlyDateTemplate}
                                header={t('TABLE_LABEL_ARRIVAL')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '10%' }}
                            />
                            <Column
                                field='Departure'
                                headerClassName='px-3'
                                body={onlyDateTemplate}
                                header={t('TABLE_LABEL_DEPARTURE')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '10%' }}
                            />
                            <Column
                                field='Status'
                                body={statusTemplate}
                                headerClassName='px-3'
                                header={t('TABLE_LABEL_STATUS')}
                                className='fs-md px-2'
                                sortable
                                style={{ width: '12.5%' }}
                            />
                            <Column
                                headerClassName='px-2 text-center'
                                header={t('TABLE_LABEL_ACTION')}
                                className='fs-md px-0 text-center'
                                body={actionTemplate}
                                style={{ width: '16%' }}
                            />
                        </DataTable>
                        <div className='pb-4'>
                            <Paginator
                                className={total > rows ? 'row pagination mx-0 d-block mh-50px' : 'd-none'}
                                rows={rows}
                                totalRecords={total}
                                first={first}
                                onPageChange={onPage}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <AddEditBooking ref={addEditBooking} onChange={onChangeBooking} />
            <Tooltip
                ref={tooltip}
                className='init-tooltip group-tooltip'
                at='center bottom'
                my='left-8 top'
                style={{ visibility: 'hidden' }}
            />
        </React.Fragment>
    );
}
